import { Injectable } from '@angular/core';
import {
	ApplicationType,
	ChemicalRevision,
} from '@context/shared/types/chemical';
import { User } from '@context/shared/types/user';
import { sanitize } from '@context/shared/utils';
import {
	collection,
	CollectionReference,
	doc,
	DocumentReference,
	getDocs,
	query,
	setDoc,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';

@Injectable({ providedIn: 'root' })
export class ApplicationTypeService {
	static readonly CollectionId = 'applicationTypes';

	/**
	 * Gets the collection reference for application types under a revision
	 */
	private getApplicationTypeCollection(
		revisionRef: DocumentReference<ChemicalRevision>,
	): CollectionReference<ApplicationType, ApplicationType> {
		return collection(
			revisionRef,
			ApplicationTypeService.CollectionId,
		) as CollectionReference<ApplicationType, ApplicationType>;
	}

	/**
	 * Fetches all application types for a given revision
	 */
	async getApplicationTypes(
		revisionRef: DocumentReference<ChemicalRevision>,
	): Promise<ApplicationType[]> {
		const applicationTypeCollection =
			this.getApplicationTypeCollection(revisionRef);
		const snapshot = await getDocs(
			query(applicationTypeCollection, where('deletedAt', '==', null)),
		);

		return snapshot.docs.map(
			(doc) =>
				({
					...doc.data(),
					id: doc.id,
				}) as ApplicationType,
		);
	}

	/**
	 * Creates an application type under a revision
	 */
	createApplicationType(
		revisionRef: DocumentReference<ChemicalRevision>,
		payload: Partial<ApplicationType>,
	): Promise<ApplicationType> {
		const now = Timestamp.now();
		const applicationType: ApplicationType = {
			id: crypto.randomUUID(),
			name: payload.name!,
			reiHours: payload.reiHours!,
			createdAt: now,
			updatedAt: now,
			deletedAt: null,
			organizationId: payload.organizationId!,
			createdBy: payload.createdBy,
		};

		const applicationTypeCollection =
			this.getApplicationTypeCollection(revisionRef);
		const applicationTypeRef = doc(
			applicationTypeCollection,
			applicationType.id,
		);

		return setDoc(applicationTypeRef, sanitize(applicationType)).then(
			() => applicationType,
		);
	}

	/**
	 * Updates an existing application type
	 */
	updateApplicationType(
		revisionRef: DocumentReference<ChemicalRevision>,
		payload: Partial<ApplicationType>,
	): Promise<Partial<ApplicationType>> {
		if (!payload.id) throw new Error('Application type ID is required');

		const now = Timestamp.now();
		const applicationTypeCollection =
			this.getApplicationTypeCollection(revisionRef);
		const applicationTypeRef = doc(applicationTypeCollection, payload.id);

		payload.updatedAt = now;
		return updateDoc(applicationTypeRef, sanitize(payload)).then(
			() => payload,
		);
	}

	/**
	 * Deletes an application type
	 */
	deleteApplicationType(
		revisionRef: DocumentReference<ChemicalRevision>,
		applicationTypeId: string,
	): Promise<string> {
		const applicationTypeCollection =
			this.getApplicationTypeCollection(revisionRef);
		const applicationTypeRef = doc(
			applicationTypeCollection,
			applicationTypeId,
		);

		const now = Timestamp.now();
		return updateDoc(
			applicationTypeRef,
			sanitize({ deletedAt: now, updatedAt: now }),
		).then(() => applicationTypeId);
	}

	/**
	 * Saves multiple application types for a revision
	 * Creates new ones, updates existing ones, and deletes removed ones
	 */
	async saveApplicationTypes(
		revisionRef: DocumentReference<ChemicalRevision>,
		applicationTypes: Partial<ApplicationType>[],
		organizationId: string,
		userRef?: DocumentReference,
	): Promise<void> {
		// Get existing application types
		const existing = await this.getApplicationTypes(revisionRef);
		const existingIds = new Set(existing.map((at) => at.id));

		// Track which IDs we're keeping
		const newIds = new Set(
			applicationTypes.filter((at) => at.id).map((at) => at.id!),
		);

		const tasks: Promise<unknown>[] = [];
		// Soft delete removed application types
		for (const existingAppType of existing) {
			if (!newIds.has(existingAppType.id)) {
				tasks.push(
					this.deleteApplicationType(revisionRef, existingAppType.id),
				);
			}
		}

		// Create or update application types
		for (const appType of applicationTypes) {
			if (appType.id && existingIds.has(appType.id)) {
				// Update existing
				tasks.push(this.updateApplicationType(revisionRef, appType));
			} else {
				// Create new
				tasks.push(
					this.createApplicationType(revisionRef, {
						...appType,
						organizationId,
						createdBy: userRef as DocumentReference<User>,
					}),
				);
			}
		}

		await Promise.all(tasks);
	}
}
